import React, { type ComponentProps, lazy, Suspense, useEffect, type ReactNode } from 'react'
import { NotFound, Route, Switch, useParams } from '@fs/zion-router'
import { AppLayout, colors, useContainerWidth } from '@fs/zion-ui'
import ErrorBoundary from '@fs/zion-error-boundary'
import { GroupsProvider, useGroupsContext, setLastViewedPid, setLastViewedPersonView } from '@fs/zion-tree-groups'
import TreeRoute from '@fs/zion-tree-route'
import { SWRConfig } from 'swr'
import { usePageViewEvent } from '@fs/zion-analytics'
import { pidRegexStr } from '@fs/zion-pid'
import { SelectSiblingPedigreeButton, SelectSiblingPedigreeOverlay } from '@fs/zion-tree-pedigree'
import { useFeatureFlag, useFeatureFlagWithPrefs } from '@fs/zion-flags'
import { usePermission } from '@fs/zion-permissions'
import { css } from '@emotion/core'
import RedirectToDetails from './helpers/RedirectToDetails'
import PersonAppProviders from './providers/PersonAppProviders'
import CenteredLoadingSpinner from './common/CenteredLoadingSpinner'
import useGroupOverlay from './helpers/useGroupOverlay'
import usePageDetailPrefix from './hooks/usePageDetailPrefix'
import AppLoading from './common/AppLoading'
import SubnavWrapper from './common/SubnavWrapper'
import ShowTempleProvider from './providers/ShowTempleContext'
import PageContentWidthContext, { sizes } from './person-page/PageContentWidthContext'
import OptInV2 from './merge/new-merge/OptInV2'
import OptOutV2 from './merge/new-merge/OptOutV2'
import { MERGE_ROUTE_PATH } from './merge/new-merge/mergeRouteUtil'
import { useContrastMode } from './ContrastModeProvider'
import useNewMergeFlag from './merge/new-merge/useNewMergeFlag'

const mainFlexCss = css`
  display: flex;
  flex-grow: 1;
  flex-direction: column;
`

const Merge = lazy(() => import('./merge/MergePage'))
const Unmerge = lazy(() => import('./unmerge/UnmergePage'))
const ChangeLog = lazy(() => import('./changelog/ChangeLog'))
const PersonPage = lazy(() => import('./person-page/PersonPage'))
const ResearchHelp = lazy(() => import('./research-help/ResearchHelp'))
const DevPage = lazy(() => import('./dev-page/DevPage'))
const PersonPrintView = lazy(() => import('./person-print-view/PersonPrintView'))
const MergeAnalysis = lazy(() => import('./merge-analysis/MergeAnalysis'))
const SplitAnalysis = lazy(() => import('./split-analysis/SplitAnalysis'))
const QualityScoreSegments = lazy(() => import('./quality-score-segments/QualityScoreSegments'))

const subPages = [
  'details',
  'ordinances',
  'about',
  'sources',
  'memories',
  'vitals',
  'non-vitals',
  'family',
  'tools',
  'search-records',
  'collaborate',
  'timeline',
]

const PersonPageWrapper = (): JSX.Element => {
  useGroupOverlay()
  const { subPage, pid } = useParams()
  const { currentTreeId } = useGroupsContext()
  const sendPageView = usePageViewEvent()
  const pageDetailPrefix = usePageDetailPrefix()

  // page_detail updates link_page for all analytics past this point
  useEffect(() => {
    if (pageDetailPrefix !== undefined) {
      sendPageView({ page_detail: `${pageDetailPrefix}Person:${subPage}` })
    }
  }, [pageDetailPrefix, sendPageView, subPage])

  useEffect(() => {
    if (currentTreeId) {
      setLastViewedPid('')
    } else {
      setLastViewedPid(pid)
    }
  }, [pid, currentTreeId])

  useEffect(() => {
    subPage && setLastViewedPersonView(subPage)
  }, [subPage])

  // TODO update Suspense for PersonPage with skeleton
  return (
    <Suspense fallback={<CenteredLoadingSpinner />}>
      <PersonPage />
    </Suspense>
  )
}

const DEV_PATH = '/'
const RESEARCH_HELP_PATH = `/research-help/:pid(${pidRegexStr})`
const CHANGELOG_PATHS = [
  `/changelog/:pid(${pidRegexStr})/:relationshipType(parent-child|couple|association)/:relationshipId`,
  `/changelog/:pid(${pidRegexStr})/:filterType?`,
]
const MERGE_ANALYSIS_PATH = `/changelog/merge-analysis/:survivorPid(${pidRegexStr})/:duplicatePid(${pidRegexStr})`
const SPLIT_ANALYSIS_PATH = `/changelog/split-analysis/:combinedPid(${pidRegexStr})/:extractedPid(${pidRegexStr})`
const PERSON_PATH = `/:subPage(${subPages.join('|')})/:pid(${pidRegexStr})`
const REDIRECT_TO_DETAILS_PATHS = [`/:pid(${pidRegexStr})`, `/:subPage(${subPages.join('|')})`, '/']
const PRINT_VIEW_PATH = `/print-view/:pid(${pidRegexStr})/`
const UNMERGE_PATH = `/unmerge/:pid(${pidRegexStr})`

const CenteredLoadingSpinnerWrapper = (): JSX.Element => <CenteredLoadingSpinner />

/** Tree Route lazy loads ErrorPage without a suspense, so we need to wrap it in suspense here. */
const TreeRouteWithSuspense = ({ LoadingComponent, ...props }: ComponentProps<typeof TreeRoute>): JSX.Element => (
  <Suspense fallback={LoadingComponent ? <LoadingComponent /> : <CenteredLoadingSpinner />}>
    <TreeRoute {...props} LoadingComponent={LoadingComponent} />
  </Suspense>
)

type GroupsLoadingGateProps = {
  children: ReactNode
  loader: ReactNode
}

/** Gates children on GroupsProvider loading state. Must be rendered inside GroupsProvider. */
const GroupsLoadingGate = ({ children, loader }: GroupsLoadingGateProps): JSX.Element => {
  const { loading } = useGroupsContext()
  if (loading) {
    return <>{loader}</>
  }
  return <>{children}</>
}

// Wraps a route in AppLayout so the background color is applied immediately on URL match,
// before any async loading (tree auth, lazy chunks) inside TreeRouteWithSuspense begins.
// Switch matches on the `path` prop of any React element, so this works as a Switch child.
// When `loader` is provided, children are gated on GroupsProvider loading (must be inside GroupsProvider).
type RouteWithLayoutProps = {
  path: string | string[]
  backgroundColor: string
  exact?: boolean
  loader?: ReactNode
  children: ReactNode
}

const RouteWithLayout = ({ path, backgroundColor, exact, loader, children }: RouteWithLayoutProps): JSX.Element => (
  <Route path={path} exact={exact}>
    <AppLayout fullWidth backgroundColor={backgroundColor}>
      {loader === undefined ? children : <GroupsLoadingGate loader={loader}>{children}</GroupsLoadingGate>}
    </AppLayout>
  </Route>
)

export default function App(): JSX.Element {
  const atWidth = useContainerWidth()
  const { isOn: advancedUnmergeIsOn } = useFeatureFlag('treeWeb_personR9_advancedUnmerge')
  const [canModifyPrivateSpaceEntities] = usePermission('ModifyAllPrivateSpaceEntitiesPermission')
  const newMergeAnalysisFlag = useFeatureFlagWithPrefs(
    'treeWeb_personR9_newMergeAnalysis',
    {},
    { userPreferences: ['fslabs.labs'] }
  )
  const newMergeFlag = useNewMergeFlag()
  const { isHighContrast } = useContrastMode()
  const backgroundColorForHighContrast = isHighContrast ? colors.gray05 : colors.gray03

  return (
    <SWRConfig value={{ revalidateOnFocus: process.env.NODE_ENV !== 'development' }}>
      <ErrorBoundary>
        <ShowTempleProvider>
          <PageContentWidthContext.Provider value={atWidth(sizes)}>
            <Switch>
              {/* /tree/person/print-view/ */}
              <RouteWithLayout path={PRINT_VIEW_PATH} exact backgroundColor={colors.gray00}>
                <TreeRouteWithSuspense LoadingComponent={CenteredLoadingSpinner} exact path={PRINT_VIEW_PATH}>
                  <GroupsProvider
                    // TODO update GroupsProvider to accept ReactNode so we don't need to have a wrapper for CenteredLoadingSpinner
                    LoadingComponent={CenteredLoadingSpinnerWrapper}
                    SelectSiblingPedigreeButton={SelectSiblingPedigreeButton}
                    SelectSiblingPedigreeOverlay={SelectSiblingPedigreeOverlay}
                  >
                    <main id="skipToMainContent" css={mainFlexCss}>
                      <PersonAppProviders loadingComponent={<CenteredLoadingSpinner />}>
                        <Suspense fallback={<CenteredLoadingSpinner />}>
                          <PersonPrintView />
                        </Suspense>
                      </PersonAppProviders>
                    </main>
                  </GroupsProvider>
                </TreeRouteWithSuspense>
              </RouteWithLayout>

              <Route path="*">
                <GroupsProvider
                  noLoadingGating
                  LoadingComponent={AppLoading}
                  SelectSiblingPedigreeButton={SelectSiblingPedigreeButton}
                  SelectSiblingPedigreeOverlay={SelectSiblingPedigreeOverlay}
                >
                  <SubnavWrapper />
                  <main id="skipToMainContent" css={mainFlexCss}>
                    <Switch>
                      {/* Checking NODE_ENV here guarantees that this will be stripped out in the production build. */}
                      {process.env.NODE_ENV === 'development' && (
                        <RouteWithLayout
                          path={DEV_PATH}
                          exact
                          backgroundColor={backgroundColorForHighContrast}
                          loader={<CenteredLoadingSpinner />}
                        >
                          <Suspense fallback={<CenteredLoadingSpinner />}>
                            <DevPage />
                          </Suspense>
                        </RouteWithLayout>
                      )}

                      {/* /tree/person/research-help/ */}
                      <RouteWithLayout
                        path={RESEARCH_HELP_PATH}
                        exact
                        backgroundColor={colors.gray00}
                        loader={<CenteredLoadingSpinner />}
                      >
                        <TreeRouteWithSuspense
                          LoadingComponent={CenteredLoadingSpinner}
                          exact
                          path={RESEARCH_HELP_PATH}
                        >
                          <PersonAppProviders loadingComponent={<CenteredLoadingSpinner />}>
                            {/* TODO update Suspense for research help with skeleton */}
                            <Suspense fallback={<CenteredLoadingSpinner />}>
                              <ResearchHelp />
                            </Suspense>
                          </PersonAppProviders>
                        </TreeRouteWithSuspense>
                      </RouteWithLayout>

                      {/* /tree/person/changelog/ */}
                      <RouteWithLayout
                        path={CHANGELOG_PATHS}
                        exact
                        backgroundColor={colors.gray00}
                        loader={<CenteredLoadingSpinner />}
                      >
                        <TreeRouteWithSuspense LoadingComponent={CenteredLoadingSpinner} exact path={CHANGELOG_PATHS}>
                          {/* TODO update Suspense for changelog with skeleton */}
                          <Suspense fallback={<CenteredLoadingSpinner />}>
                            <ChangeLog />
                          </Suspense>
                        </TreeRouteWithSuspense>
                      </RouteWithLayout>

                      {/* /tree/person/changelog/merge-analysis/ */}
                      <RouteWithLayout
                        path={MERGE_ANALYSIS_PATH}
                        exact
                        backgroundColor={newMergeAnalysisFlag.data?.isOn ? colors.gray05 : colors.gray00}
                        loader={<CenteredLoadingSpinner />}
                      >
                        <TreeRouteWithSuspense
                          LoadingComponent={CenteredLoadingSpinner}
                          exact
                          path={MERGE_ANALYSIS_PATH}
                        >
                          {/* TODO update Suspense for MergeAnalysis with skeleton */}
                          <Suspense fallback={<CenteredLoadingSpinner />}>
                            <MergeAnalysis />
                          </Suspense>
                        </TreeRouteWithSuspense>
                      </RouteWithLayout>

                      {/* /tree/person/changelog/split-analysis/ */}
                      <RouteWithLayout
                        path={SPLIT_ANALYSIS_PATH}
                        exact
                        backgroundColor={colors.gray00}
                        loader={<CenteredLoadingSpinner />}
                      >
                        <TreeRouteWithSuspense
                          LoadingComponent={CenteredLoadingSpinner}
                          exact
                          path={SPLIT_ANALYSIS_PATH}
                        >
                          {/* TODO update Suspense for SplitAnalysis with skeleton */}
                          <Suspense fallback={<CenteredLoadingSpinner />}>
                            <SplitAnalysis />
                          </Suspense>
                        </TreeRouteWithSuspense>
                      </RouteWithLayout>

                      {/* /quality-score/segments */}
                      <RouteWithLayout
                        path="/quality-score/segments"
                        exact
                        backgroundColor={backgroundColorForHighContrast}
                        loader={<CenteredLoadingSpinner />}
                      >
                        <TreeRouteWithSuspense exact path="/quality-score/segments">
                          <Suspense fallback={<CenteredLoadingSpinner />}>
                            <QualityScoreSegments />
                          </Suspense>
                        </TreeRouteWithSuspense>
                      </RouteWithLayout>

                      {/* /tree/person/merge/v2/opt-in */}
                      <RouteWithLayout
                        path="/merge/v2/opt-in"
                        exact
                        backgroundColor={backgroundColorForHighContrast}
                        loader={<CenteredLoadingSpinner />}
                      >
                        <TreeRouteWithSuspense exact path="/merge/v2/opt-in">
                          <OptInV2 />
                        </TreeRouteWithSuspense>
                      </RouteWithLayout>

                      {/* /tree/person/merge/v2/opt-out */}
                      <RouteWithLayout
                        path="/merge/v2/opt-out"
                        exact
                        backgroundColor={backgroundColorForHighContrast}
                        loader={<CenteredLoadingSpinner />}
                      >
                        <TreeRouteWithSuspense exact path="/merge/v2/opt-out">
                          <OptOutV2 />
                        </TreeRouteWithSuspense>
                      </RouteWithLayout>

                      {/* /tree/person/merge/ */}
                      <RouteWithLayout
                        path={MERGE_ROUTE_PATH}
                        exact
                        backgroundColor={
                          newMergeFlag.loading || newMergeFlag.data?.isOn ? colors.gray00 : colors.gray05
                        }
                        loader={<CenteredLoadingSpinner />}
                      >
                        <TreeRouteWithSuspense LoadingComponent={CenteredLoadingSpinner} exact path={MERGE_ROUTE_PATH}>
                          {/* TODO update Suspense for Merge with skeleton */}
                          <Suspense fallback={<CenteredLoadingSpinner />}>
                            <Merge />
                          </Suspense>
                        </TreeRouteWithSuspense>
                      </RouteWithLayout>

                      {/* /tree/person/unmerge/ */}
                      {advancedUnmergeIsOn && canModifyPrivateSpaceEntities && (
                        <RouteWithLayout
                          path={UNMERGE_PATH}
                          exact
                          backgroundColor={backgroundColorForHighContrast}
                          loader={<CenteredLoadingSpinner />}
                        >
                          <TreeRouteWithSuspense exact path={UNMERGE_PATH}>
                            {/* TODO update Suspense for Unmerge with skeleton */}
                            <Suspense fallback={<CenteredLoadingSpinner />}>
                              <Unmerge />
                            </Suspense>
                          </TreeRouteWithSuspense>
                        </RouteWithLayout>
                      )}

                      {/* Person Subpages (details, ordinances, about, sources, etc.) */}
                      <RouteWithLayout
                        path={PERSON_PATH}
                        exact
                        backgroundColor={backgroundColorForHighContrast}
                        loader={<CenteredLoadingSpinner />}
                      >
                        <TreeRouteWithSuspense LoadingComponent={CenteredLoadingSpinner} exact path={PERSON_PATH}>
                          <PersonAppProviders loadingComponent={<CenteredLoadingSpinner />}>
                            <PersonPageWrapper />
                          </PersonAppProviders>
                        </TreeRouteWithSuspense>
                      </RouteWithLayout>

                      {/* Redirect various paths to the person details view (or entered subpage) */}
                      <TreeRouteWithSuspense LoadingComponent={AppLoading} exact path={REDIRECT_TO_DETAILS_PATHS}>
                        <RedirectToDetails />
                      </TreeRouteWithSuspense>

                      {/* 404 Not Found */}
                      <Route path="*">
                        <AppLayout fullWidth backgroundColor={backgroundColorForHighContrast}>
                          <NotFound />
                        </AppLayout>
                      </Route>
                    </Switch>
                  </main>
                </GroupsProvider>
              </Route>
            </Switch>
          </PageContentWidthContext.Provider>
        </ShowTempleProvider>
      </ErrorBoundary>
    </SWRConfig>
  )
}
